import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api } from '../api/client';
import type { Page, UserRow } from '../api/types';
import { Badge, Button, Card, Field, Loading, Metric, Notice, Select, Table } from '../components';
import { useAction, useResource } from '../hooks';
import { useSession } from '../auth/session';

/**
 * How each status is said out loud.
 *
 * The stored values are shouted enum constants, which is right in the database and wrong on
 * a screen. `SCHEDULED` in particular has to be read as "not started" here: a supervisor
 * arriving from the dashboard is asking which visits have not been begun, and the word the
 * database uses for that answers a different question.
 */
const STATUS_LABELS: Record<string, string> = {
  SCHEDULED: 'Not started',
  IN_PROGRESS: 'In progress',
  COMPLETE: 'Completed',
  FROZEN: 'Frozen',
  NOT_PERFORMED: 'Not visited',
};

interface ScheduledVisit {
  id: string;
  scheduledDate: string;
  status: string;
  siteCode: string;
  siteName: string;
  fieldUserName: string;
  fieldUserId: string;
}

interface PlannedAssignment {
  siteCode: string;
  siteName: string;
  siteId: string;
  scheduledDate: string;
  sequence: number;
}

interface GenerationPlan {
  assignments: PlannedAssignment[];
  nextIndex: number;
  nextCycleNumber: number;
  workingDayCount: number;
  skipped: { nonWorkingDays: number; calendarExceptions: number; alreadyAssigned: number };
  excludedSiteCount: number;
  cycleRestarted: boolean;
  createdCount?: number;
}

/**
 * Generate a schedule, after seeing exactly what it would create.
 *
 * Preview and generate call the same computation — the generator is a pure function, so
 * what appears here *is* what the commit writes, not an approximation of it. SMA had no
 * equivalent: generation wrote directly, so the only way to find out what it would do was
 * to let it do it and then clean up.
 *
 * The skipped counts matter as much as the assignments. A plan that quietly produced
 * fewer visits than expected, with no statement of why, is how a coverage gap goes
 * unnoticed for a month.
 */
export function Schedule(): JSX.Element {
  const { can, config } = useSession();
  const { run, error, busy } = useAction();

  const [fieldUserId, setFieldUserId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [plan, setPlan] = useState<GenerationPlan | null>(null);
  const [committed, setCommitted] = useState(false);
  const [openVisitId, setOpenVisitId] = useState<string | null>(null);

  /**
   * Arrived here from a figure on the dashboard.
   *
   * The narrowing lives in the URL rather than in component state so the screen is the same
   * one whether it was reached by a link, a bookmark or the back button. A dashboard tile
   * that opens a filtered list and then loses the filter on reload is worse than one that
   * does not link at all: the second is a dead end, the first is a wrong answer.
   */
  const [params, setParams] = useSearchParams();
  const focusDate = params.get('date');
  const focusStatus = params.get('status');
  const clearFocus = (): void => {
    setParams({});
  };

  const users = useResource(() => api.get<Page<UserRow>>('/users?pageSize=200'), []);

  // What is already booked. Reloaded after generating, so the screen answers the question
  // a supervisor asks straight afterwards: what did that actually put in the calendar?
  /**
   * Which month the calendar shows. A flat list of every visit ever booked is not something
   * anyone reads — the question is always "what does this person have in March?".
   */
  const [monthOffset, setMonthOffset] = useState(0);
  const month = monthRange(monthOffset);

  // A single day when the dashboard sent us here, the whole month otherwise. Narrowing the
  // request rather than the rendering means the count below is the count of what was asked
  // for, not of what happened to be fetched.
  const range = focusDate === null ? month : { from: focusDate, to: focusDate };

  const booked = useResource(
    () =>
      api.get<ScheduledVisit[]>(
        `/assignments?from=${range.from}&to=${range.to}` +
          (fieldUserId === '' ? '' : `&fieldUserId=${fieldUserId}`),
      ),
    [fieldUserId, range.from, range.to],
  );

  /**
   * Status is applied here rather than in the request.
   *
   * A day holds a handful of visits, so filtering five rows in the browser costs nothing —
   * and the alternative, a status parameter on the list endpoint, would be a second way of
   * asking the same question that has to agree with the first forever.
   */
  const visible = (booked.data ?? []).filter(
    (visit) => focusStatus === null || visit.status === focusStatus,
  );

  /** Grouped by person, then sorted by date — the shape the screen reads in. */
  const byPerson = new Map<string, ScheduledVisit[]>();
  for (const visit of visible) {
    const list = byPerson.get(visit.fieldUserName) ?? [];
    list.push(visit);
    byPerson.set(visit.fieldUserName, list);
  }
  for (const list of byPerson.values()) {
    list.sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
  }

  const preview = async (): Promise<void> => {
    setCommitted(false);
    await run(async () => {
      setPlan(await api.post<GenerationPlan>('/schedule/preview', { fieldUserId, from, to }));
    });
  };

  const generate = async (): Promise<void> => {
    const ok = await run(async () => {
      setPlan(await api.post<GenerationPlan>('/schedule/generate', { fieldUserId, from, to }));
    });
    if (ok) {
      setCommitted(true);
      booked.reload();
    }
  };

  const ready = fieldUserId !== '' && from !== '' && to !== '';

  return (
    <>
      {/* The booked-visit list below is useful to a store manager and is scoped to their
          own places. Generating is not theirs to do, so the form is not offered. */}
      {can('assignment.reschedule') && (
      <Card title="Generate a schedule">
        {error !== null && <Notice kind="error">{error}</Notice>}
        {committed && <Notice kind="success">Schedule written. {plan?.createdCount ?? 0} assignment(s) created.</Notice>}

        <div className="toolbar">
          {users.loading ? (
            <Loading />
          ) : (
            <Select
              label={config.fieldUserLabel}
              value={fieldUserId}
              onChange={setFieldUserId}
              required
              options={(users.data?.items ?? []).map((user) => ({
                value: user.id,
                label: `${user.fullName} (${user.username})`,
              }))}
            />
          )}
          <Field label="From" value={from} onChange={setFrom} type="date" required />
          <Field label="To" value={to} onChange={setTo} type="date" required />
          <Button variant="secondary" onClick={() => void preview()} disabled={busy || !ready}>
            Preview
          </Button>
          <Button onClick={() => void generate()} disabled={busy || !ready || plan === null || committed}>
            {busy ? 'Working…' : 'Generate'}
          </Button>
        </div>

        <p className="muted">
          Preview first. Generating writes visits and advances the coverage cycle, so the
          next period continues where this one stops rather than restarting. A place already
          booked in the period is never booked again.
        </p>
      </Card>
      )}

      <Card
        title="Booked visits"
        actions={
          /* The month controls would silently widen a single-day view back out, so while one
             is focused the only control offered is the one that leaves it. */
          focusDate !== null ? (
            <Button variant="secondary" onClick={clearFocus}>
              Show the whole month
            </Button>
          ) : (
            <div className="row">
              <Button variant="secondary" onClick={() => setMonthOffset(monthOffset - 1)}>
                ‹ Previous
              </Button>
              <Button variant="secondary" onClick={() => setMonthOffset(0)}>
                This month
              </Button>
              <Button variant="secondary" onClick={() => setMonthOffset(monthOffset + 1)}>
                Next ›
              </Button>
            </div>
          )
        }
      >
        {/* Said in full, because a filtered list that does not announce its filter is read
            as the whole truth — and here that would mean reading one status as the day. */}
        <p className="muted">
          {focusDate === null ? month.label : focusDate}
          {focusStatus === null ? '' : ` · ${STATUS_LABELS[focusStatus] ?? focusStatus} only`} ·{' '}
          {visible.length} visit(s)
          {fieldUserId === '' ? ` across ${byPerson.size} ${config.fieldUserLabel.toLowerCase()}(s)` : ''}
        </p>

        {booked.loading ? (
          <Loading />
        ) : booked.error !== null ? (
          <Notice kind="error">{booked.error}</Notice>
        ) : byPerson.size === 0 ? (
          <p className="empty">
            {/* The label is quoted rather than folded into the sentence: two of the five are
                themselves negative, and "no visit is not visited" reads as a riddle. */}
            {focusStatus === null
              ? `Nothing booked ${focusDate === null ? `in ${month.label}` : `on ${focusDate}`}.`
              : `Nothing on ${focusDate} is marked “${STATUS_LABELS[focusStatus] ?? focusStatus}”.`}
          </p>
        ) : (
          /* One section per person rather than one long table. A supervisor checking cover
             is asking about a person's month, not about the estate's. */
          [...byPerson.entries()].map(([name, visits]) => (
            <details key={name} open={byPerson.size <= 3}>
              <summary style={{ cursor: 'pointer', padding: '8px 0', fontWeight: 600 }}>
                {name} — {visits.length} visit(s)
              </summary>

              <Table
                rows={visits}
                keyOf={(row) => row.id}
                empty="Nothing booked."
                onRowClick={(row) => setOpenVisitId(row.id)}
                columns={[
                  { header: 'Date', render: (row) => row.scheduledDate },
                  {
                    header: config.siteLabel,
                    render: (row) => `${row.siteCode} — ${row.siteName}`,
                  },
                  {
                    header: 'Status',
                    render: (row) => (
                      <Badge tone={row.status}>{STATUS_LABELS[row.status] ?? row.status}</Badge>
                    ),
                  },
                ]}
              />
            </details>
          ))
        )}
      </Card>

      {openVisitId !== null && (
        <VisitDetail id={openVisitId} onClose={() => setOpenVisitId(null)} onChanged={booked.reload} />
      )}

      {plan !== null && (
        <>
          <div className="metrics">
            <Metric label="Assignments planned" value={plan.assignments.length} />
            <Metric label="Working days" value={plan.workingDayCount} />
            <Metric label="Non-working days skipped" value={plan.skipped.nonWorkingDays} />
            <Metric label="Calendar exceptions" value={plan.skipped.calendarExceptions} />
            <Metric label="Dates already booked" value={plan.skipped.alreadyAssigned} />
            <Metric label={`${config.siteLabel}s excluded by revisit window`} value={plan.excludedSiteCount} />
          </div>

          {/* Said plainly, because the numbers above otherwise contradict the table below:
              "4 excluded" followed by those same four appearing in the plan. */}
          {plan.cycleRestarted && (
            <Notice kind="info">
              Every {config.siteLabel.toLowerCase()} available to this{' '}
              {config.fieldUserLabel.toLowerCase()} was visited inside the revisit window, so the
              cycle started again rather than leaving the period empty. The rotation still covers
              every {config.siteLabel.toLowerCase()} before repeating one.
            </Notice>
          )}

          <Card title={committed ? 'What was created' : 'What would be created'}>
            <Table
              rows={plan.assignments}
              keyOf={(row) => `${row.scheduledDate}-${row.siteId}-${row.sequence}`}
              empty="Nothing would be scheduled in this period."
              columns={[
                { header: 'Date', render: (row) => row.scheduledDate },
                { header: 'Order in day', numeric: true, render: (row) => row.sequence + 1 },
                {
                  header: config.siteLabel,
                  render: (row) => `${row.siteCode} — ${row.siteName}`,
                },
              ]}
            />
          </Card>
        </>
      )}
    </>
  );
}

/** The calendar month `offset` months from now, as the API's date range. */
function monthRange(offset: number): { from: string; to: string; label: string } {
  const anchor = new Date();
  anchor.setDate(1);
  anchor.setMonth(anchor.getMonth() + offset);

  const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);

  const iso = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  return {
    from: iso(start),
    to: iso(end),
    label: anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
  };
}

/**
 * One visit, and the one thing a supervisor does about a closed one.
 *
 * Re-opening is here rather than on a separate screen because this is where somebody looking
 * at a month notices a visit that reads NOT_PERFORMED and wants to know why — and, if the
 * van broke down, to put it back.
 */
function VisitDetail({
  id,
  onClose,
  onChanged,
}: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
}): JSX.Element {
  const visit = useResource(() => api.get<VisitDetailRow>(`/assignments/${id}`), [id]);
  const { run, error, busy } = useAction();
  const [reason, setReason] = useState('');

  const reopen = async (): Promise<void> => {
    const ok = await run(async () => {
      await api.post(`/assignments/${id}/reopen`, { reason });
    });
    if (ok) {
      setReason('');
      visit.reload();
      onChanged();
    }
  };

  const closed = visit.data?.status === 'FROZEN' || visit.data?.status === 'NOT_PERFORMED';

  return (
    <Card
      title={visit.data === null ? 'Visit' : `${visit.data.siteCode} — ${visit.data.siteName}`}
      actions={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {error !== null && <Notice kind="error">{error}</Notice>}

      {visit.loading ? (
        <Loading />
      ) : visit.error !== null ? (
        <Notice kind="error">{visit.error}</Notice>
      ) : visit.data === null ? (
        <p className="empty">That visit could not be loaded.</p>
      ) : (
        <>
          <div className="row">
            <Badge tone={visit.data.status}>
              {STATUS_LABELS[visit.data.status] ?? visit.data.status}
            </Badge>
            <span className="muted">{visit.data.scheduledDate}</span>
            <span className="muted">{visit.data.fieldUserName}</span>
          </div>

          {visit.data.reopenedReason !== null && (
            <Notice kind="info">
              Re-opened previously: {visit.data.reopenedReason}
            </Notice>
          )}

          {closed && (
            <>
              <p className="muted">
                {visit.data.status === 'NOT_PERFORMED'
                  ? 'This visit was never started and the day closed. Re-opening puts it back on the schedule.'
                  : 'This visit was under way when the day closed. Re-opening lets the work be finished.'}
              </p>

              <Field
                label="Why is it being re-opened?"
                value={reason}
                onChange={setReason}
                required
                hint="Recorded on the audit trail against this visit."
              />

              <div className="form-actions">
                <Button onClick={() => void reopen()} disabled={busy || reason.trim() === ''}>
                  {busy ? 'Re-opening…' : 'Re-open this visit'}
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </Card>
  );
}

interface VisitDetailRow {
  id: string;
  status: string;
  scheduledDate: string;
  siteCode: string;
  siteName: string;
  fieldUserName: string;
  reopenedReason: string | null;
}
